import { HttpClient, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { RequestModel } from '../typings/typing';

@Injectable({
  providedIn: 'root',
})
export class ClaimService {
  prefix = '/dealer_portal/claim';
  metaPrefix = '/dealer_portal/claimMeta';

  constructor(private http: HttpClient) {}

  apiClaimPage(params: any) {
    return this.http.post<RequestModel>(this.prefix + '/page', params);
  }

  apiClaimOverview() {
    return this.http.get<RequestModel>(this.prefix + '/overview');
  }

  apiDeleteClaimDraft(id: number | string) {
    return this.http.delete<RequestModel>(this.prefix + '/draft/' + id);
  }

  apiClaimCopy(id: number | string) {
    return this.http.post<RequestModel>(this.prefix + '/copy/' + id, {});
  }

  apiClaimDetail(id: number | string) {
    return this.http.get<RequestModel>(this.prefix + '/' + id);
  }

  apiCustomerDocTypeList() {
    return this.http.get<RequestModel>('/dealer_portal/customerDocType/list');
  }

  apiClaimOrderSearch(body: any) {
    return this.http.post<RequestModel>('/dealer_portal/claimOrder/search', body);
  }

  apiClaimInvoiceSearch(body: any) {
    return this.http.post<RequestModel>(
      '/dealer_portal/claimInvoice/search',
      body,
    );
  }

  apiClaimExport(query: any): Observable<HttpResponse<Blob>> {
    return this.http.post(this.prefix + '/export', query, {
      observe: 'response',
      responseType: 'blob',
    });
  }

  apiClaimTypes() {
    return this.http.get<RequestModel>(this.metaPrefix + '/claimTypes');
  }

  apiClaimCategories(claimTypeName?: string) {
    const params: { [param: string]: string } = {};
    if (claimTypeName) {
      params['claimTypeName'] = claimTypeName;
    }
    return this.http.get<RequestModel>(this.metaPrefix + '/claimCategories', {
      params,
    });
  }

  apiClaimSolutions() {
    return this.http.get<RequestModel>(this.metaPrefix + '/claimSolutions');
  }

  /**
   * 上传 Claim 附件 — 透传到 BSP，返回 BspAttachmentVO
   * （含 attachmentId / fileName / postedDate）。
   * 后端字段名固定为 `file`（multipart/form-data）。
   */
  apiClaimAttachmentUpload(file: File) {
    const form = new FormData();
    form.append('file', file);
    return this.http.post<RequestModel>(
      '/dealer_portal/claimAttachment/upload',
      form,
    );
  }

  /**
   * 下载 Claim 附件 — 后端流式写响应体（非 ResponseData 包装），
   * 因此用 blob 响应类型并保留 headers 以便从
   * Content-Disposition 推断文件名。
   */
  apiClaimAttachmentDownload(
    attachmentId: string,
    fileName?: string,
  ): Observable<HttpResponse<Blob>> {
    const params: { [param: string]: string } = {};
    if (fileName) {
      params['fileName'] = fileName;
    }
    return this.http.get(
      `/dealer_portal/claimAttachment/download/${encodeURIComponent(attachmentId)}`,
      {
        params,
        observe: 'response',
        responseType: 'blob',
      },
    );
  }

  /**
   * 保存 Claim 草稿。后端入参 `ClaimSaveReqVO`：除 `referType` 外其它
   * 字段均可空；返回 body.data 为本地 `claim_info.id`。
   */
  apiClaimSaveDraft(body: any) {
    return this.http.post<RequestModel>(this.prefix + '/saveDraft', body);
  }

  /**
   * 提交 Claim（推送 BSP）。后端入参 `ClaimSaveReqVO`，完整校验；
   * 返回 body.data 为本地 `claim_info.id`。
   */
  apiClaimSubmit(body: any) {
    return this.http.post<RequestModel>(this.prefix + '/submit', body);
  }

  apiClaimUpdateTrackingNum(body: any) {
    return this.http.post<RequestModel>(this.prefix + '/editTrackingNum', body);
  }

  apiClaimAttachmentDelete(attachmentId: string) {
    return this.http.delete<RequestModel>(
      `/dealer_portal/claimAttachment/${encodeURIComponent(attachmentId)}`,
    );
  }

  apiClaimMetaCascade(body: {
    claimTypeName?: string | null;
    claimCategoryName?: string | null;
    referType?: string | null;
    docType?: string | null;
    claimSolutionName?: string | null;
  }) {
    return this.http.post<RequestModel>(this.metaPrefix + '/cascade', body);
  }
}
